import { ReactNode } from 'react';
import { Dialog, Heading, Modal, ModalOverlay } from 'react-aria-components';
import { Button } from './Button';
import { Text, typography } from './Text';
import { cx } from './cx';
import { useT } from '../../modules/i18n';

export interface ConfirmDialogProps {
  open: boolean;
  /** Called when the dialog is dismissed (cancel / escape / backdrop). */
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Use the destructive button treatment (e.g. reject/delete). */
  destructive?: boolean;
  /** While true the buttons are disabled and the dialog can't be dismissed. */
  busy?: boolean;
  onConfirm: () => void;
  /** Extra body content (e.g. a form control) rendered below `description`,
   * outside the `Text`/`<p>` wrapper so block-level children (a `Select`,
   * an `Alert`) never nest inside a paragraph. */
  children?: ReactNode;
}

/**
 * Accessible confirmation modal (react-aria Modal/Dialog) on the design-system
 * primitives. Foundation tokens only — the scrim uses `bg-overlay`, the panel
 * `bg-surface`/`border-default`. Controlled via `open`.
 */
export const ConfirmDialog = ({
  open,
  onClose,
  title,
  description,
  confirmLabel,
  cancelLabel,
  destructive = false,
  busy = false,
  onConfirm,
  children,
}: ConfirmDialogProps) => {
  const t = useT();
  const resolvedConfirmLabel = confirmLabel ?? t('ui.confirmDialog.confirm');
  const resolvedCancelLabel = cancelLabel ?? t('ui.confirmDialog.cancel');
  return (
    <ModalOverlay
      isOpen={open}
      onOpenChange={isOpen => {
        if (!isOpen) onClose();
      }}
      isDismissable={!busy}
      isKeyboardDismissDisabled={busy}
      className="fixed inset-0 z-50 flex items-center justify-center bg-overlay p-4"
    >
      <Modal className="w-full max-w-md rounded-xl border border-default bg-surface shadow-lg outline-none">
        <Dialog role="alertdialog" className="p-5 outline-none">
          <Heading slot="title" className={cx(typography.title, 'text-fg')}>
            {title}
          </Heading>
          {description && (
            <Text variant="body" tone="secondary" className="mt-2">
              {description}
            </Text>
          )}
          {children && <div className="mt-4">{children}</div>}
          <div className="mt-5 flex justify-end gap-2">
            <Button
              variant="secondary"
              size="sm"
              isDisabled={busy}
              onPress={onClose}
            >
              {resolvedCancelLabel}
            </Button>
            <Button
              variant={destructive ? 'destructive' : 'primary'}
              size="sm"
              isDisabled={busy}
              onPress={onConfirm}
            >
              {resolvedConfirmLabel}
            </Button>
          </div>
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
};
